import { Link, Outlet, useLocation } from "react-router-dom";
import { formatPhone } from "../../utils/formatPhone";
import { useIsAuthenticated, useAuthLoading } from "@/hooks/auth/useAuth";
import { useTenant } from "../../contexts/TenantContext";
import { useState } from "react";
import { Menu, X, LayoutDashboard } from "lucide-react";
import NavLinks from "./NavLinks";
import ClaimedLeadCard from "../shared/ClaimedLeadCard";
import "./logo.css";

// Default logo configuration for when branding.logo is missing
const DEFAULT_LOGO = {
  url: "/placeholder-logo.png",
  height: 40,
  width: 200,
  placement: "header" as const,
};

export const PublicLayout = () => {
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const { config: tenantConfig, error } = useTenant();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isAILandingPage =
    location.pathname === "/homes-for-sale" ||
    location.pathname === "/homes-for-sale/results";

  // Check if current page is an LP page
  const isLPPage =
    location.pathname === "/lp" || location.pathname.startsWith("/lp/");

  // Check if current page is a city/feature page (/{city-slug}/{feature-slug})
  const isCityFeaturePage = (() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    return pathSegments.length >= 2 && pathSegments[0].includes("-");
  })();

  // Don't add margin-top for AI landing pages, LP pages, or city/feature pages
  const shouldAddMarginTop =
    !isAILandingPage && !isLPPage && !isCityFeaturePage;

  const config = tenantConfig || {
    branding: {
      colors: {
        background: "#ffffff",
        text: "#000000",
        primary: "#000000",
      },
      logo: {
        url: "/placeholder-logo.png",
        height: 40,
        width: 200,
        placement: "header" as const,
      },
      footer: {
        license: "",
      },
    },
    name: "Property Search",
    orgData: {
      org_name: "",
      org_phone: "",
      client_address: {
        street: "",
        city: "",
        state: "",
        zip: "",
      },
      org_links: {} as Record<string, string>,
    },
  };

  // Use default styles while loading or if there's an error
  const styles = {
    // Page background is always white regardless of tenant config.
    backgroundColor: "#ffffff",
    borderColor: `${config.branding.colors.text}11`,
  };

  // Show error message in header instead of blocking entire layout
  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={styles}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">
            Error loading tenant configuration: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={styles}>
      {/* Navigation - hidden for AI Landing page */}
      {!isAILandingPage && (
        <nav
          className="flex-shrink-0 h-20 lg:h-24 border-b fixed top-0 left-0 right-0 z-20"
          style={{
            backgroundColor: "#ffffff",
            borderColor: `${config.branding.colors.text}11`,
          }}
        >
          <div className="max-w-[1300px] 3xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20 lg:h-24">
              <div className="flex flex-1">
                <div
                  className="flex-shrink-0 flex items-center"
                  style={{ minWidth: "fit-content" }}
                >
                  <Link to="/" className="flex items-center h-full">
                    <div
                      className="flex items-center px-2"
                      style={{
                        minWidth: "fit-content",
                        height: "80px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={config.branding.logo?.url || DEFAULT_LOGO.url}
                        alt={config.name}
                        className="logo-image"
                        style={{
                          height: `${
                            config.branding.logo?.height || DEFAULT_LOGO.height
                          }px`,
                          maxWidth: `${
                            config.branding.logo?.width || DEFAULT_LOGO.width
                          }px`,
                        }}
                      />
                    </div>
                  </Link>
                </div>
              </div>
              <div className="flex items-center flex-shrink-0">
                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="sm:hidden inline-flex items-center justify-center p-2 rounded-md !bg-transparent text-primary"
                >
                  {isMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>

                <div className="hidden sm:flex sm:items-center space-x-4">
                  <NavLinks />
                  <div
                    className="flex items-center space-x-4"
                    style={{ minWidth: "200px" }}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_0ms]"
                          style={{
                            backgroundColor: config.branding.colors.primary,
                          }}
                        ></div>
                        <div
                          className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_200ms]"
                          style={{
                            backgroundColor: config.branding.colors.primary,
                          }}
                        ></div>
                        <div
                          className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_400ms]"
                          style={{
                            backgroundColor: config.branding.colors.primary,
                          }}
                        ></div>
                      </div>
                    ) : isAuthenticated ? (
                      <Link
                        to="/dashboard"
                        className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white btn-primary"
                        style={{
                          backgroundColor: config.branding.colors.primary,
                          transition: "all 0.2s",
                        }}
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/auth/login"
                          className="px-4 py-2 rounded-lg text-sm font-medium nav-link"
                          style={{
                            color: config.branding.colors.primary,
                            transition: "all 0.2s",
                          }}
                        >
                          Sign in
                        </Link>
                        <Link
                          to="/auth/register"
                          className="text-xs xl:text-sm ml-4 inline-flex items-center px-4 2xl:px-6 py-2 border border-transparent font-medium rounded-lg shadow-sm text-white btn-primary"
                          style={{
                            backgroundColor: config.branding.colors.primary,
                            transition: "all 0.2s",
                          }}
                        >
                          Sign up
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Mobile menu - hidden for AI Landing page */}
      {!isAILandingPage && isMenuOpen && (
        <div
          className="sm:hidden fixed top-20 inset-x-0 z-40 bg-white shadow-lg border-b max-h-[calc(100vh-5rem)] overflow-y-auto"
          style={{
            borderColor: `${config.branding.colors.text}11`,
          }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            <NavLinks isMobile onMobileClick={() => setIsMenuOpen(false)} />
            <div className="min-h-[40px]">
              {" "}
              {/* Maintain consistent height */}
              {isLoading ? (
                <div className="px-3 py-2 flex items-center space-x-2">
                  <div
                    className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_0ms]"
                    style={{ backgroundColor: config.branding.colors.primary }}
                  ></div>
                  <div
                    className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_200ms]"
                    style={{ backgroundColor: config.branding.colors.primary }}
                  ></div>
                  <div
                    className="w-1.5 h-1.5 rounded-full animate-[bounce_1s_infinite_400ms]"
                    style={{ backgroundColor: config.branding.colors.primary }}
                  ></div>
                </div>
              ) : isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 rounded-md text-sm font-medium"
                  style={{
                    color: config.branding.colors.primary,
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/auth/login"
                    className="block px-3 py-2 rounded-md text-sm font-medium"
                    style={{
                      color: config.branding.colors.primary,
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/auth/register"
                    className="block px-3 py-2 rounded-md text-sm font-medium"
                    style={{
                      color: config.branding.colors.primary,
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 ${shouldAddMarginTop ? "mt-20 lg:mt-24" : ""}`}>
        <Outlet />
      </main>

      {/* Floating Agent Card - shown when user has a claimed lead */}
      <ClaimedLeadCard />

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 xl:py-12">
        <div className="w-full mx-auto max-w-[1400px] px-6">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 md:items-stretch">
            {/* Left Section - Logo & Copyright */}
            <div className="flex flex-col items-center md:items-start md:pr-8 md:border-r md:border-gray-300">
              {/* Logo - shown when placement is 'both' */}
              {config.branding.logo?.placement === "both" && (
                <img
                  src={config.branding.logo.url}
                  alt={config.name}
                  className="footer-logo-image mb-2"
                  style={{
                    height: "40px",
                    maxWidth: "150px",
                  }}
                />
              )}

              {/* Copyright */}
              <p className="text-sm lg:text-base font-medium text-center md:text-left">
                &copy; {new Date().getFullYear()} {config.name}.
              </p>
              <p className="text-xs lg:text-sm text-gray-500 text-center md:text-left">
                All rights reserved.
              </p>
            </div>

            {/* Middle Section - Contact Info */}
            <div className="flex flex-col items-center md:items-start md:px-8">
              {/* Office Address */}
              {config.orgData?.client_address && (
                <div className="text-xs lg:text-sm text-gray-900 text-center md:text-left">
                  {config.orgData.client_address.street && (
                    <p>{config.orgData.client_address.street}</p>
                  )}
                  {(config.orgData.client_address.city ||
                    config.orgData.client_address.state ||
                    config.orgData.client_address.zip) && (
                    <p>
                      {[
                        config.orgData.client_address.city,
                        config.orgData.client_address.state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      {config.orgData.client_address.zip &&
                        ` ${config.orgData.client_address.zip}`}
                    </p>
                  )}
                </div>
              )}

              {/* Office Phone */}
              {config.orgData?.org_phone && (
                <a
                  href={`tel:${config.orgData.org_phone}`}
                  className="text-xs lg:text-sm text-gray-900 hover:text-gray-900 mt-1 underline"
                >
                  {formatPhone(config.orgData.org_phone)}
                </a>
              )}

              {/* License - Below phone */}
              {config.branding?.footer?.license && (
                <p className="text-xs lg:text-sm text-gray-900 text-center md:text-left mt-1">
                  DRE No. {config.branding.footer.license}
                </p>
              )}
            </div>

            {/* Right Section - Links */}
            <div className="flex flex-col items-center md:items-end">
              <div className="flex flex-col items-center md:items-end gap-1">
                <Link
                  to="/terms"
                  className="text-xs lg:text-sm text-gray-700 hover:text-gray-900 underline"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/privacy"
                  className="text-xs lg:text-sm text-gray-700 hover:text-gray-900 underline"
                >
                  Privacy Policy
                </Link>

                {/* Compliance Links */}
                {config.orgData?.org_links &&
                  Object.keys(config.orgData.org_links).length > 0 &&
                  Object.entries(config.orgData.org_links).map(
                    ([title, url]) => (
                      <a
                        key={title}
                        href={url as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs lg:text-sm text-gray-700 hover:text-gray-900 underline"
                      >
                        {title}
                      </a>
                    ),
                  )}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
